import type { AxiosInstance } from 'axios';
import type { paths } from '../schema';
import type { TimelineEntry } from '../types/timeline';
import type { CustomerNotificationFilter } from './customer-notifications';
import type { RequestOptions, RequestOptionsWithParams } from './types';
import { curry1 } from './utils';

export type EmployeePageConfig<T extends EmployeePageConfigType> = {
  config?: EmployeePageConfigs[T] | null;
};

export type EmployeePageConfigType =
  | 'activity_list'
  | 'ad_manager_list'
  | 'automation_list'
  | 'billing'
  | 'broadcast_calendar'
  | 'custom_object_list'
  | 'dashboard_list'
  | 'form_list'
  | 'homepage_list'
  | 'knowledge_crawl_configs_list'
  | 'knowledge_crawled_pages_list'
  | 'knowledge_files_list'
  | 'lead_source_cost_list'
  | 'message_library_list'
  | 'notification_center'
  | 'open_activities_list'
  | 'smart_connector_list'
  | 'survey_list'
  | 'toolbar';

type KnowledgePageConfig = {
  page_size?: number;
  ordering?: string;
  search?: string;
  filters?: Record<string, { value: string; label: string } | null>;
};

export type EmployeePageConfigs = {
  activity_list: Record<string, unknown>;
  ad_manager_list: Record<string, unknown>;
  automation_list: Record<string, unknown>;
  billing: Record<string, unknown>;
  broadcast_calendar: Record<string, unknown>;
  custom_object_list: Record<string, unknown>;
  dashboard_list: Record<string, unknown>;
  form_list: Record<string, unknown>;
  homepage_list: Record<string, unknown>;
  knowledge_crawl_configs_list: KnowledgePageConfig;
  knowledge_crawled_pages_list: KnowledgePageConfig;
  knowledge_files_list: KnowledgePageConfig;
  lead_source_cost_list: Record<string, unknown>;
  notification_center: {
    archived?: 'archived' | 'all';
    created?: [string, string];
    has_comments?: boolean;
    mentions?: 'is_me' | string;
    notification_type?: Array<
      CustomerNotificationFilter<'notification_type'>['options'][number]['value']
    >;
    read?: boolean;
    scope?: 'business' | 'employee';
    search?: string;
    status?: Array<CustomerNotificationFilter<'status'>['options'][number]['value']>;
  };
  message_library_list: Record<string, unknown>;
  open_activities_list: Record<string, unknown>;
  smart_connector_list: Record<string, unknown>;
  survey_list: Record<string, unknown>;
  toolbar: Record<string, unknown>;
};

type PageConfig = Required<
  paths['/api/employee/mine/configs/custom-objects/{object_pk}']['post']
>['responses']['200']['content']['application/json'];

export interface EmployeeCustomObjectConfig extends PageConfig {
  column_config?: {
    column_order: string[];
    columns?: Record<
      string,
      {
        disabled: boolean;
        field_id: string;
        id: string;
        label: string;
        sortable: boolean;
        visible: boolean;
        width: string;
      }
    >;
  };
  record_list_page_config?: {
    filter_object?:
      | { and: boolean; query: { and: boolean; filters: any[]; id: string }[] }
      | Record<string, never>;
    group?: string | null;
    page?: number;
    query_filter?: Record<string, any>;
    quick_filters?: Record<string, any>;
    search?: string;
    size?: number;
    sort?: string;
    view_variant?: 'board' | 'charts' | 'table';
  };
}

export type EmployeeTimelineEntry = TimelineEntry<
  Record<string, any>,
  | 'employee_created'
  | 'employee_verified'
  | 'employee_archived'
  | 'employee_unarchived'
  | 'employee_email_confirmed'
  | 'employee_password_updated'
  | 'employee_access_updated'
  | 'employee_email_suppressed'
>;

export type EmployeeHistoryParams = Required<
  paths['/api/employee/{employee_id}/history']['get']['parameters']
>['query'];

export type EmployeeHistory = Omit<
  paths['/api/employee/{employee_id}/history']['get']['responses']['200']['content']['application/json'],
  'results'
> & {
  results: EmployeeTimelineEntry[];
};

export const getEmployeeCustomObjectConfig = async (
  instance: AxiosInstance,
  customObjectId: string,
  options: RequestOptions = {}
): Promise<EmployeeCustomObjectConfig> => {
  const res = await instance.get(
    `/employee/mine/configs/custom-objects/${customObjectId}`,
    options
  );

  return res.data;
};

export const getEmployeePageConfig = async <T extends EmployeePageConfigType>(
  instance: AxiosInstance,
  type: T,
  options: RequestOptions = {}
): Promise<EmployeePageConfig<T>> => {
  const res = await instance.get(
    `/employee/mine/configs/page/${type}`,
    options
  );

  return res.data;
};

export const getEmployeeHistory = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptionsWithParams<EmployeeHistoryParams>
): Promise<EmployeeHistory> => {
  const res = await instance.get<EmployeeHistory>(
    `/employee/${id}/history`,
    options
  );

  return res.data;
};

export const updateEmployeePageConfig = async <
  T extends EmployeePageConfigType,
>(
  instance: AxiosInstance,
  type: T,
  data: { config: EmployeePageConfigs[T] },
  options: RequestOptions = {}
): Promise<EmployeePageConfig<T>> => {
  const res = await instance.put(
    `/employee/mine/configs/page/${type}`,
    data,
    options
  );

  return res.data;
};

export const updateEmployeeCustomObjectConfig = async (
  instance: AxiosInstance,
  customObjectId: string,
  data: EmployeeCustomObjectConfig,
  options: RequestOptions = {}
): Promise<EmployeeCustomObjectConfig> => {
  const res = await instance.post(
    `/employee/mine/configs/custom-objects/${customObjectId}`,
    data,
    options
  );

  return res.data;
};

export class EmployeeService {
  public getEmployeeCustomObjectConfig;
  public getEmployeePageConfig;
  public getEmployeeHistory;
  public updateEmployeeCustomObjectConfig;
  public updateEmployeePageConfig;

  constructor(private instance: AxiosInstance) {
    this.getEmployeeCustomObjectConfig = curry1(getEmployeeCustomObjectConfig)(
      this.instance
    );
    this.getEmployeePageConfig = curry1(getEmployeePageConfig)(this.instance);
    this.getEmployeeHistory = curry1(getEmployeeHistory)(this.instance);
    this.updateEmployeeCustomObjectConfig = curry1(
      updateEmployeeCustomObjectConfig
    )(this.instance);
    this.updateEmployeePageConfig = curry1(updateEmployeePageConfig)(
      this.instance
    );
  }
}
